import {useCallback, useRef, useState} from 'react';
import {CoreError, type MultiPayment} from '@chekin/core';
import {StripeError} from '@stripe/stripe-js';
import {StatusModalStates} from '../../../components';
import {useTimeoutRef} from '@chekinapp/ui';

const SuccessModalTimeoutMs = 2800;
const LoadingModalTimeoutMs = 1500;

function getStripeCode(reason: unknown) {
  if (!reason || typeof reason !== 'object') return undefined;
  const {decline_code: declineCode, code} = reason as Partial<StripeError>;
  return declineCode ?? code;
}

type Props = {
  onSuccess: () => void;
  autoDismissSuccess?: boolean;
};
export const usePaymentProcessingState = ({
  onSuccess,
  autoDismissSuccess = true,
}: Props) => {
  const [processState, setProcessStateRaw] = useState(StatusModalStates.IDLE);
  const [error, setError] = useState<string>();
  const [paidMultiPayment, setPaidMultiPayment] = useState<MultiPayment>();
  const [progress, setProgress] = useState<MultiPayment>();
  const [isBankConfirming, setIsBankConfirming] = useState(false);
  const [failureCode, setFailureCode] = useState<string>();
  const loadingModalTimeoutRef = useTimeoutRef();
  const successModalTimeoutRef = useTimeoutRef();
  const isSettledRef = useRef(false);

  const setProcessState = useCallback((nextState: StatusModalStates) => {
    if (nextState === StatusModalStates.LOADING) {
      isSettledRef.current = false;
      setProgress(undefined);
      setFailureCode(undefined);
    }
    setProcessStateRaw(nextState);
  }, []);

  const closeProcessingModal = useCallback(() => {
    isSettledRef.current = false;
    setProcessStateRaw(StatusModalStates.IDLE);
    setError('');
    setPaidMultiPayment(undefined);
    setProgress(undefined);
    setIsBankConfirming(false);
    setFailureCode(undefined);
  }, []);

  const onLoadingModal = () => {
    return new Promise<void>(resolve => {
      setProcessStateRaw(StatusModalStates.LOADING);
      loadingModalTimeoutRef.current = setTimeout(() => {
        return resolve();
      }, LoadingModalTimeoutMs);
    });
  };

  const onPaymentSuccess = (multiPayment: MultiPayment) => {
    if (isSettledRef.current) return;
    isSettledRef.current = true;
    setProgress(multiPayment);

    onLoadingModal().then(() => {
      setPaidMultiPayment(multiPayment);
      setProcessStateRaw(StatusModalStates.SUCCESS);

      if (!autoDismissSuccess) {
        onSuccess();
        return;
      }

      successModalTimeoutRef.current = setTimeout(() => {
        closeProcessingModal();
        onSuccess();
      }, SuccessModalTimeoutMs);
    });
  };

  const onPaymentError = (
    reason: string | Error | CoreError | StripeError,
    errorMsg?: string,
    multiPayment?: MultiPayment,
  ) => {
    if (isSettledRef.current) return;
    isSettledRef.current = true;

    if (multiPayment) setProgress(multiPayment);
    setIsBankConfirming(false);
    setFailureCode(getStripeCode(reason));
    setError(errorMsg);
    setProcessStateRaw(StatusModalStates.ERROR);
  };

  return {
    processState,
    error,
    paidMultiPayment,
    progress,
    isBankConfirming,
    failureCode,
    onProgress: setProgress,
    onBankConfirmation: setIsBankConfirming,
    onPaymentSuccess,
    onPaymentError,
    setProcessState,
    closeProcessingModal,
  };
};
